import DashboardLayout from "../../lib/components/layouts/dashboard";
import PatientConfirmAppointment from "lib/components/appointments/patient/patient-confirm-appointment";
import { useContext, useEffect, useState } from "react";
import { BookingContext } from "lib/contexts/booking-context";
import usePatientBookings from "lib/hooks/usePatientBookings";
import { toast } from "sonner";
import { AppContext } from "lib/contexts/app-context";
import { isPatientUser, openUrlInSameTab } from "lib/utils/util";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import usePractitioner from "lib/hooks/usePractitioner";
import {
  areDatesEqual,
  formatDate,
  getDatePart,
  getDateWithTime,
  getLocalISO,
  getUTCISO,
} from "lib/utils/date-utils";
import BookingService from "lib/services/appointment-booking-service";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";

const PatientConfirmAppointmentPage = () => {
  const { appState } = useContext(AppContext);
  const navigateTo = useNavigate();

  const BreadcrumbItems = [
    {
      label: "Dashboard",
      path: isPatientUser(appState?.userRole)
        ? PAGE_ROUTES.PATIENT.Dashboard
        : PAGE_ROUTES.PRACTITIONER.Dashboard,
    },
    {
      label: "Book Appointment",
      path: isPatientUser(appState?.userRole)
        ? PAGE_ROUTES.PATIENT.BookAppointment
        : PAGE_ROUTES.PRACTITIONER.BookAppointment,
    },
    {
      label: "Confirm Appointment",
      path: PAGE_ROUTES.PATIENT.BookAppointment,
    },
  ] as BreadcrumbItem[];

  const [subscriptions, setSubscriptions] = useState<
    PractitionerSubscriptionDetails[]
  >([]);
  const [bookingSlots, setBookingSlots] = useState<AppointmentBooking[]>([]);
  const [bookingSummary, setBookingSummary] =
    useState<AppointmentBookingDetails | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const { bookingState } = useContext<BookingState>(BookingContext);

  const {
    getPractitionerSubscriptions,
    postPractitionerSubscriptions,
    loading: practitionerLoading,
    error: practitionerError,
  } = usePractitioner();
  const { appointments, getAppointments, bookAppointment, loading, error } =
    usePatientBookings();

  const refreshData = () => {
    setSelectedDate(null);
    setBookingSlots([]);
    setBookingSummary(null);
    getAppointments(bookingState?.practitioner?.id);
    postPractitionerSubscriptions(
      bookingState?.clinic?.id,
      bookingState?.practitioner?.id,
      setSubscriptions
    );
  };

  useEffect(() => {
    // Only load data if user is authenticated
    if (appState?.accessJWT && bookingState?.practitioner?.id) {
      refreshData();
    }
  }, [appState?.accessJWT, bookingState?.practitioner?.id]);

  const handleDateSelection = (value: Date | null) => {
    setSelectedDate(value);
    const existingAppointments = appointments.filter(
      (appointment) =>
        appointment.clinicId === bookingState?.clinic?.id &&
        areDatesEqual(getDatePart(new Date(appointment.fromTime)), value)
    );

    const bookingTimes = existingAppointments?.map((appointment) => {
      const fromTime = BookingService.formatDateAsAppointmentBookingTime(
        appointment.fromTime
      );
      const toTime =
        BookingService.formatDateAsAppointmentBookingTimeAndDuration(
          appointment.fromTime,
          appointment.duration
        );

      const duration = BookingService.getDurationFromBooking(fromTime, toTime);

      return {
        fromTime: fromTime, //AppointmentBookingTime;
        toTime: toTime, //AppointmentBookingTime;
        isBooked: true,
        appointmentId: appointment.id,
        duration: duration, // Duration in minutes
      } as AppointmentBooking;
    });

    const bookingTime = BookingService.getAppointmentBooking(
      bookingState?.clinic?.availableHours,
      value
    );

    const sessionTime = Number(bookingState?.therapy?.sessionTime);

    let availableSlots =
      BookingService.getAvailableBookingSlotsWithBookedSlots(
        value,
        getDateWithTime(value, bookingTime?.fromTime),
        getDateWithTime(value, bookingTime?.toTime),
        sessionTime === 0 ? 20 : sessionTime,
        bookingTimes
      );

    // Filter out past slots if today is selected
    if (value && areDatesEqual(value, new Date())) {
      const now = new Date();
      availableSlots = availableSlots.filter((slot) => {
        const slotTime = getDateWithTime(value, slot.fromTime);
        return slotTime.getTime() > now.getTime();
      });
    }

    setBookingSlots(availableSlots);
  };

  const handleSlotSelection = (selectedSlot: AppointmentBooking) => {
    if (selectedSlot) {
      const bookingDetails: AppointmentBookingDetails = {
        clinicName: bookingState?.clinic?.name,
        clinicId: bookingState?.clinic?.id,
        practitionerId: bookingState?.practitioner?.id,
        practitionerName: `${bookingState?.practitioner?.firstName} ${bookingState?.practitioner?.lastName}`,
        therapyId: bookingState?.therapy?.id,
        therapyName: bookingState?.therapy?.name,
        fromTime: getUTCISO(
          getDateWithTime(selectedDate, selectedSlot.fromTime)
        ),
        patientId: appState?.user_id,
        patientName: appState?.name,
        duration:
          selectedSlot.duration ?? Number(bookingState?.therapy?.sessionTime),
        totalCost: Number(bookingState?.therapy?.perSessionPrice),
        status: "pending",
        fromTimeUTC: getLocalISO(getDateWithTime(selectedDate, selectedSlot.fromTime)),
      };

      setBookingSummary(bookingDetails);
      //bookAppointment(bookingDetails);
    }
  };

  const handleConfirmBooking = (bookingDetails: AppointmentBookingDetails) => {
    // Use the bookingSummary state instead of the parameter
    const detailsToBook = bookingSummary || bookingDetails;
    
    console.log('handleConfirmBooking called with:', bookingDetails);
    console.log('bookingSummary state:', bookingSummary);
    console.log('detailsToBook:', detailsToBook);
    
    if (!detailsToBook) {
      toast.error("Please select a valid booking slot.");
      return;
    }

    bookAppointment(detailsToBook, (response) => {
      console.log('bookAppointment response:', response);
      if (response) {
        toast.success("Appointment booked successfully!");
        // Redirect to success page with complete booking details
        // Use the bookingSummary as it contains all the information we need
        const successState = {
          bookingDetails: {
            ...detailsToBook,
            id: response.id, // Use the ID from the response if available
            status: response.status || detailsToBook.status
          }
        };
        console.log('Navigating to success page with state:', successState);
        navigateTo(PAGE_ROUTES.PATIENT.BookingSuccess, {
          state: successState
        });
      }
    });
  };

  /** Calculated Vars */
  // Step 1 => Raw DateTimes
  let practitionerAvailableDateTimes = (subscriptions ?? [])
    .flatMap((subscription) => subscription.dates)
    .map((date) => DateTime.fromISO(date))
    .filter((dt) => dt.isValid)
    .sort((a, b) => a.toMillis() - b.toMillis());

  // Step 2 => Remove Sundays if clinic has sundayClose = true
  if (bookingState?.clinic?.sundayClose) {
    practitionerAvailableDateTimes = practitionerAvailableDateTimes.filter(
      (dt) => dt.weekday !== 7 // 7 = Sunday
    );
  }

  // Step 3 => Convert to formatted string array for the UI
  const practitionerAvailableDates: string[] =
    practitionerAvailableDateTimes.map((dt) =>
      dt.toFormat("ccc. dd-LLL-yyyy")
    );

  /** Calculated Vars */

  if (error || practitionerError) {
    return (
      <DashboardLayout breadcrumbItems={BreadcrumbItems}>
        <div className="text-center">
          <h4>Error loading appointment details</h4>
          <p className="text-muted">{error || practitionerError}</p>
        </div>
      </DashboardLayout>
    );
  }

  // Show loading or authentication message
  if (!appState?.accessJWT) {
    return (
      <DashboardLayout breadcrumbItems={BreadcrumbItems}>
        <div className="text-center">
          <h4>Please log in to access appointment confirmation.</h4>
        </div>
      </DashboardLayout>
    );
  }

  // If subscriptions are loaded and empty, show a message
  if (subscriptions && subscriptions.length === 0) {
    return (
      <DashboardLayout breadcrumbItems={BreadcrumbItems}>
        <div className="text-center">
          <h4>Practitioner has not booked a room at any clinic.</h4>
          <p className="text-muted">Please select another practitioner for booking.</p>
          <button 
            className="btn btn-primary mt-3"
            onClick={() => navigateTo(PAGE_ROUTES.PATIENT.BookAppointment)}
          >
            Go Back to Booking
          </button>
        </div>
      </DashboardLayout>
    );
  }
  
  // Check if booking state is properly initialized
  if (!bookingState?.practitioner?.id || !bookingState?.clinic?.id || !bookingState?.therapy?.id) {
    return (
      <DashboardLayout breadcrumbItems={BreadcrumbItems}>
        <div className="text-center">
          <h4>Please select a practitioner, clinic, and therapy first.</h4>
          <button 
            className="btn btn-primary mt-3"
            onClick={() => navigateTo(PAGE_ROUTES.PATIENT.BookAppointment)}
          >
            Go Back to Booking
          </button>
        </div>
      </DashboardLayout>
    );
  }

  if (loading || practitionerLoading) {
    return (
      <DashboardLayout breadcrumbItems={BreadcrumbItems}>
        <div className="text-center">
          <h4>Loading appointment details...</h4>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout breadcrumbItems={BreadcrumbItems}>
      <PatientConfirmAppointment
        practitionerDates={practitionerAvailableDates}
        bookingSlots={bookingSlots}
        bookingSummary={bookingSummary}
        onDateSelected={handleDateSelection}
        onSlotSelected={handleSlotSelection}
        onBookingConfirmed={handleConfirmBooking}
      />
    </DashboardLayout>
  );
};

export default PatientConfirmAppointmentPage;
